import { useCallback, useEffect, useRef, type MutableRefObject } from 'react'

type Args = {
  requiresSmsOtp: boolean
  setStatusMessage: (message: string) => void
  startResendCooldown: () => void
  stateIsCode: boolean
}

type CodeStepFocusResult = {
  codeInputRef: MutableRefObject<HTMLInputElement | null>
  hasVisitedCodeStepRef: MutableRefObject<boolean>
  resetCodeStepTracking: () => void
}

export function useCodeStepFocus({
  stateIsCode,
  requiresSmsOtp,
  startResendCooldown,
  setStatusMessage,
}: Args): CodeStepFocusResult {
  const wasCodeStepRef = useRef(false)
  const hasVisitedCodeStepRef = useRef(false)
  const codeInputRef = useRef<HTMLInputElement | null>(null)

  const resetCodeStepTracking = useCallback(() => {
    hasVisitedCodeStepRef.current = false
    wasCodeStepRef.current = false
  }, [])

  useEffect(() => {
    if (stateIsCode) {
      hasVisitedCodeStepRef.current = true
    }
    const justEnteredCodeStep = stateIsCode && !wasCodeStepRef.current
    if (justEnteredCodeStep && requiresSmsOtp) {
      startResendCooldown()
      setStatusMessage('Verification code sent. Enter the 6-digit code from SMS.')
      window.requestAnimationFrame(() => {
        codeInputRef.current?.focus()
      })
    } else if (justEnteredCodeStep && !requiresSmsOtp) {
      window.requestAnimationFrame(() => {
        codeInputRef.current?.focus()
      })
    }
    wasCodeStepRef.current = stateIsCode
  }, [stateIsCode, startResendCooldown, requiresSmsOtp, setStatusMessage])

  return { codeInputRef, hasVisitedCodeStepRef, resetCodeStepTracking }
}
